// Founder Command Center home — rendered at /<cohortSlug>/founder.
//
// Renders real company data when the snapshot is ready (Crezam case),
// or empty waiting states when the founder is brand new.
//
// Tone rules from PC: plain English, no slang, no metaphors, no
// motivational lines, no GPT sing-song. Numerals not words.
import { useEffect, useState } from "react";
import {
  TrendingUp, Gauge, MessagesSquare, ArrowRight, Sparkles,
  Target, Building2, Globe2, FileText, ShieldCheck,
} from "lucide-react";
import { Link } from "wouter";
import { SIA } from "./sia-voice";
import { useCohort, fp } from "@/lib/founder-cohort";
import AdvisorCard from "@/components/founder/advisor-card";

const BASE = import.meta.env.BASE_URL;

interface CompanySnapshot {
  id: number;
  name: string;
  description: string | null;
  sector: string | null;
  stage: string | null;
  website: string | null;
  foundedYear: number | null;
  businessModel: string | null;
  geography: string | null;
  thesis: string | null;
  thesisStructured: any | null;
  onboardingStatus: string | null;
  investorVisibilityStatus: string | null;
}

interface FounderMe {
  id: number;
  name: string;
  email: string;
  role: string;
  isPrimaryFounder: boolean;
  companyId: number;
  companyName: string | null;
  company: CompanySnapshot | null;
}

export default function FounderCommandCenter() {
  const { slug, theme } = useCohort();
  const [me, setMe] = useState<FounderMe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}api/founders/me`, {
          headers: { "x-founder-token": localStorage.getItem("founder_token") || "" },
        });
        if (res.ok) setMe(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  }
  if (!me) {
    return <div className="p-12 text-red-400 text-sm">Could not load your profile. Please sign out and sign in again.</div>;
  }

  const firstName = (me.name || "").split(" ")[0] || "Founder";
  const company = me.company;
  const hasSnapshot = !!company?.thesisStructured;
  const hasBasicProfile = !!company?.description && !!company?.sector;
  const ts = company?.thesisStructured || {};

  // Sia greeting picks itself based on company state.
  const siaGreeting = hasSnapshot
    ? SIA.greetings.snapshotReady
    : SIA.greetings.awaitingOnboarding;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">
          {company?.name || "Your company"} · {theme.shortLabel}
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Welcome back, {firstName}.</h1>
        <p className="text-white/60 mt-1 text-sm">Here is where your company stands today.</p>
      </div>

      {/* Sia greeting */}
      <div className="mb-4 bg-gradient-to-br from-[#FF5C00]/10 via-[#FF5C00]/5 to-transparent border border-[#FF5C00]/30 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF5C00]/20 border border-[#FF5C00]/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#FF5C00]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-sm font-bold">{SIA.displayName}</span>
              <span className="text-[11px] text-white/60">{SIA.role}</span>
            </div>
            <p className="text-sm text-white/85 leading-relaxed mb-2">{siaGreeting}</p>
            {hasSnapshot && (
              <Link
                href={fp(slug, "review")}
                data-testid="link-open-review-room"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#FF5C00] hover:text-[#FF5C00]/80"
              >
                Open snapshot in Review Room <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Phase 1.2 — Advisor card.
          PRD §16 Q6 hard rule: never expose breach/at_risk to founder. The
          AdvisorCard component fetches /api/founder/my-advisor which only
          surfaces visible_to_founder=true touches. Empty-state shows "will
          be assigned soon" without leaking SLA state. */}
      <div className="mb-8">
        <AdvisorCard theme="dark" />
      </div>

      {/* Company snapshot from Meera (when present) */}
      {hasBasicProfile && (
        <div className="mb-8 bg-white/[0.04] border border-white/[0.08] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-white/60" />
              <span className="text-xs font-mono uppercase tracking-wider text-white/60">Snapshot from Meera</span>
            </div>
            {hasSnapshot && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF5C00] bg-[#FF5C00]/10 border border-[#FF5C00]/30 rounded px-2 py-0.5">
                Awaiting your review
              </span>
            )}
          </div>

          <h2 className="text-2xl font-bold tracking-tight mb-1">{company?.name}</h2>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/60 mb-4">
            {company?.sector && <span>{company.sector}</span>}
            {company?.foundedYear && <span>· Founded {company.foundedYear}</span>}
            {company?.website && (
              <a href={company.website} target="_blank" rel="noreferrer" className="text-[#FF5C00]/80 hover:text-[#FF5C00]">
                {company.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            )}
          </div>

          {company?.description && (
            <p className="text-sm text-white/80 leading-relaxed mb-5">{company.description}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ts.positioning && (
              <SnapshotBlock icon={FileText} label="Positioning">
                {ts.positioning}
              </SnapshotBlock>
            )}
            {company?.businessModel && (
              <SnapshotBlock icon={Building2} label="Business model">
                {company.businessModel}
              </SnapshotBlock>
            )}
            {ts.traction?.revenue && (
              <SnapshotBlock icon={TrendingUp} label="Revenue">
                {ts.traction.revenue}
              </SnapshotBlock>
            )}
            {ts.traction?.customers && (
              <SnapshotBlock icon={Target} label="Customers">
                {ts.traction.customers}
              </SnapshotBlock>
            )}
            {company?.geography && (
              <SnapshotBlock icon={Globe2} label="Geography">
                {company.geography}
              </SnapshotBlock>
            )}
          </div>

          {Array.isArray(ts.traction?.keyDrivers) && ts.traction.keyDrivers.length > 0 && (
            <div className="mt-5 pt-5 border-t border-white/[0.08]">
              <div className="text-xs font-mono uppercase tracking-wider text-white/60 mb-2.5">
                What is working
              </div>
              <ul className="space-y-1.5">
                {ts.traction.keyDrivers.map((d: string, i: number) => (
                  <li key={i} className="text-sm text-white/75 leading-relaxed flex gap-2">
                    <span className="text-[#FF5C00] mt-0.5">·</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasSnapshot && (
            <div className="mt-5 pt-5 border-t border-white/[0.08] flex items-start gap-2 bg-[#FF5C00]/5 -mx-6 -mb-6 px-6 py-4 rounded-b-xl">
              <ShieldCheck className="w-4 h-4 text-[#FF5C00] mt-0.5 flex-shrink-0" />
              <div className="text-xs text-white/70 leading-relaxed">
                This is a draft from Meera. Nothing on this snapshot is visible to investors yet. Open Review Room to accept, correct, or reject each part. After your review, your Advisor will confirm the stage and your investor profile will be built from here.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Card
          icon={TrendingUp}
          label="Stage"
          value={hasSnapshot ? "Pending advisor confirmation" : "Awaiting onboarding"}
          hint={hasSnapshot
            ? "Meera has produced a stage hypothesis from your files. Your Advisor will confirm or correct it within the first week."
            : "After onboarding, Meera will produce a stage hypothesis. Your Advisor will then confirm or correct it."}
          cta={hasSnapshot
            ? { href: fp(slug, "review"), label: "See snapshot" }
            : { href: fp(slug, "onboarding"), label: "Start onboarding" }}
        />
        <Card
          icon={Gauge}
          label="Investor Readiness"
          value="Not yet scored"
          hint="Your readiness score appears once your snapshot is approved and your first proof file is uploaded."
          cta={{ href: fp(slug, "readiness"), label: "What goes into the score" }}
        />
        <Card
          icon={Sparkles}
          label="Your Advisor"
          value="Assignment pending"
          hint="One Advisor will be your point of contact. Same person every week. Every request will come with the reason."
          cta={{ href: fp(slug, "thread"), label: "Open Advisor Thread" }}
        />
        <Card
          icon={Target}
          label="Open requests from your Advisor"
          value="0"
          hint="When your Advisor asks for something, it will show up here with the reason."
        />
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, hint, cta }: {
  icon: any; label: string; value: string; hint: string;
  cta?: { href: string; label: string };
}) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-white/60" />
        <span className="text-xs font-mono uppercase tracking-wider text-white/60">{label}</span>
      </div>
      <div className="text-lg font-bold mb-1.5">{value}</div>
      <p className="text-sm text-white/60 mb-3 leading-relaxed">{hint}</p>
      {cta && (
        <Link href={cta.href} className="text-sm text-[#FF5C00] hover:text-[#FF5C00]/80 font-medium inline-flex items-center gap-1">
          {cta.label} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

function SnapshotBlock({ icon: Icon, label, children }: { icon: any; label: string; children: React.ReactNode }) {
  return (
    <div className="bg-black/30 border border-white/[0.06] rounded-lg p-3.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5 text-white/60" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">{label}</span>
      </div>
      <div className="text-sm text-white/80 leading-relaxed">{children}</div>
    </div>
  );
}
